import type { LucideIcon } from "lucide-react-native";
import {
  Salad,
  Leaf,
  Vegan,
  AlertTriangle,
  BookOpen,
  Beef,
  BarChart3,
  MoreHorizontal,
  Pill,
  Atom,
  Gem,
  Sprout,
} from "lucide-react-native";

// ============================================================================
// Diet types
// ============================================================================

export type DietType =
  | "all_round"
  | "vegetarian"
  | "vegan"
  | "food_hypersensitivity"
  | "religious_restriction"
  | "lchf_diet"
  | "low_gi_food"
  | "other";

export interface DietOption {
  id: DietType;
  labelKey: string;
  icon: LucideIcon;
}

export const DIET_OPTIONS: DietOption[] = [
  {
    id: "all_round",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.all_round",
    icon: Salad,
  },
  {
    id: "vegetarian",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.vegetarian",
    icon: Leaf,
  },
  {
    id: "vegan",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.vegan",
    icon: Vegan,
  },
  {
    id: "food_hypersensitivity",
    labelKey:
      "med_history.lifestyle.diet_supplements.diet_type.food_hypersensitivity",
    icon: AlertTriangle,
  },
  {
    id: "religious_restriction",
    labelKey:
      "med_history.lifestyle.diet_supplements.diet_type.religious_restriction",
    icon: BookOpen,
  },
  {
    id: "lchf_diet",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.lchf_diet",
    icon: Beef,
  },
  {
    id: "low_gi_food",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.low_gi_food",
    icon: BarChart3,
  },
  {
    id: "other",
    labelKey: "med_history.lifestyle.diet_supplements.diet_type.other",
    icon: MoreHorizontal,
  },
];

export function getDietLabelKey(diet: string): string {
  return (
    DIET_OPTIONS.find((o) => o.id === diet)?.labelKey ??
    "med_history.lifestyle.diet_supplements.diet_type.other"
  );
}

// ============================================================================
// Supplement types
// ============================================================================

export type SupplementType =
  | "folic_acid"
  | "coenzyme_q10"
  | "minerals"
  | "herbal"
  | "other";

export interface SupplementOption {
  id: SupplementType;
  labelKey: string;
  icon: LucideIcon;
}

export const SUPPLEMENT_OPTIONS: SupplementOption[] = [
  {
    id: "folic_acid",
    labelKey:
      "med_history.lifestyle.diet_supplements.supplement_type.folic_acid",
    icon: Pill,
  },
  {
    id: "coenzyme_q10",
    labelKey:
      "med_history.lifestyle.diet_supplements.supplement_type.coenzyme_q10",
    icon: Atom,
  },
  {
    id: "minerals",
    labelKey: "med_history.lifestyle.diet_supplements.supplement_type.minerals",
    icon: Gem,
  },
  {
    id: "herbal",
    labelKey: "med_history.lifestyle.diet_supplements.supplement_type.herbal",
    icon: Sprout,
  },
  {
    id: "other",
    labelKey: "med_history.lifestyle.diet_supplements.supplement_type.other",
    icon: MoreHorizontal,
  },
];

export function getSupplementLabelKey(supplement: string): string {
  return (
    SUPPLEMENT_OPTIONS.find((o) => o.id === supplement)?.labelKey ??
    "med_history.lifestyle.diet_supplements.supplement_type.other"
  );
}

// ============================================================================
// Frequency options
// ============================================================================

export type FrequencyType =
  | "at_least_5_times_week"
  | "3_4_times_week"
  | "1_2_times_week"
  | "2_3_times_month"
  | "1_time_month"
  | "1_6_times_year";

export interface FrequencyOption {
  id: FrequencyType;
  labelKey: string;
}

export const FREQUENCY_OPTIONS: FrequencyOption[] = [
  {
    id: "1_6_times_year",
    labelKey: "med_history.lifestyle.diet_supplements.frequency.1_6_times_year",
  },
  {
    id: "1_time_month",
    labelKey: "med_history.lifestyle.diet_supplements.frequency.1_time_month",
  },
  {
    id: "2_3_times_month",
    labelKey:
      "med_history.lifestyle.diet_supplements.frequency.2_3_times_month",
  },
  {
    id: "1_2_times_week",
    labelKey: "med_history.lifestyle.diet_supplements.frequency.1_2_times_week",
  },
  {
    id: "3_4_times_week",
    labelKey: "med_history.lifestyle.diet_supplements.frequency.3_4_times_week",
  },
  {
    id: "at_least_5_times_week",
    labelKey:
      "med_history.lifestyle.diet_supplements.frequency.at_least_5_times_week",
  },
];

export function getFrequencyLabelKey(frequency: string): string {
  return (
    FREQUENCY_OPTIONS.find((o) => o.id === frequency)?.labelKey ?? frequency
  );
}
